import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, OperatorFunction, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LooseObject } from 'Angular/interfaces/loose-object.interface';

import { URIComponentCodec } from './uri_component_codec';

export interface ErrorResponse {
    status: number;
    errorCode: string;
    errorMessage: string;
    requestId?: string;
    errors?: { loc: string[]; msg: string; type: string }[];
}
// TODO once we are confident that all endpoints are using this structured format then we remove this option
export const errorResponseFormatter = <T>(enabled: boolean): OperatorFunction<T, T> => {
    if (!enabled) {
        // Just pass the observable through, no interception
        return (source$) => source$;
    }

    return (source$) =>
        source$.pipe(
            catchError<T, Observable<never>>((httpErrorResponse: HttpErrorResponse) => {
                let parsedError: ErrorResponse;
                const { error } = httpErrorResponse;

                if (error && typeof error === 'object') {
                    parsedError = {
                        status: httpErrorResponse.status,
                        errorCode: error.error_code || 'UNKNOWN_ERROR',
                        errorMessage: error.error_message || httpErrorResponse.message,
                        requestId: error.request_id,
                        errors: error.errors,
                    };
                } else {
                    parsedError = {
                        status: httpErrorResponse.status,
                        errorCode: 'UNKNOWN_ERROR',
                        errorMessage: error || httpErrorResponse.message || 'Unknown error',
                    };
                }

                return throwError(() => parsedError);
            })
        );
};

@Injectable({
    providedIn: 'root',
})
export class AjaxService {
    constructor(private httpClient: HttpClient, private sanitizer: DomSanitizer) {}

    getDataObserveResponse<T>(
        url: string,
        getParams: LooseObject = {},
        additionalHeaders: LooseObject = {}
    ): Observable<HttpResponse<T>> {
        const headers = new HttpHeaders({ ...additionalHeaders, 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.get<T>(url, { headers, observe: 'response', params: getParams });
    }

    getDataObserveBody<T>(
        url: string,
        getParams: LooseObject = {},
        additionalHeaders: LooseObject = {},
        enableStructuredError = false
    ): Observable<T> {
        const headers = new HttpHeaders({ ...additionalHeaders, 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .get<T>(url, { headers, observe: 'body', params: getParams })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    getDataObserveText(
        url: string,
        getParams: LooseObject = {},
        additionalHeaders: LooseObject = {}
    ): Observable<string> {
        const headers = new HttpHeaders({ ...additionalHeaders, 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.get(url, { headers, observe: 'body', params: getParams, responseType: 'text' });
    }

    getJsonP<T>(url: string): Observable<T> {
        const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, url) ?? '';
        return this.httpClient.jsonp<T>(sanitizedUrl, 'callback');
    }

    postDataObserveBody<T>(
        url: string,
        postParams: LooseObject,
        additionalHeaders: LooseObject = {},
        enableStructuredError = false
    ): Observable<T> {
        const headers = new HttpHeaders({ ...additionalHeaders, 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .post<T>(url, postParams, { headers, responseType: 'json', observe: 'body' })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    postJsonDataObserveResponse<T>(url: string, postParams: LooseObject): Observable<HttpResponse<T>> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post<T>(url, postParams, { headers, responseType: 'json', observe: 'response' });
    }

    postJsonDataObserveBody<T>(
        url: string,
        postParams: LooseObject,
        enableStructuredError = false,
        withCredentials = false
    ): Observable<T> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .post<T>(url, postParams, { headers, responseType: 'json', observe: 'body', withCredentials })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    postFormEncodedDataObserveResponse<T>(url: string, postParams: LooseObject): Observable<HttpResponse<T>> {
        const serializedParams = new HttpParams({ fromObject: postParams });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post<T>(url, serializedParams, { headers, observe: 'response' });
    }

    postFormEncodedDataObserveBody<T>(
        url: string,
        postParams: LooseObject,
        enableStructuredError = false
    ): Observable<T> {
        const serializedParams = new HttpParams({ fromObject: postParams, encoder: new URIComponentCodec() });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .post<T>(url, serializedParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    postFormEncodedDataWithBlobObserveResponse(url: string, postParams: LooseObject): Observable<HttpResponse<Blob>> {
        const serializedParams = new HttpParams({ fromObject: postParams, encoder: new URIComponentCodec() });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post(url, serializedParams, { headers, observe: 'response', responseType: 'blob' });
    }

    postFormEncodedDataWithTextObserveResponse(url: string, postParams: LooseObject): Observable<HttpResponse<string>> {
        const serializedParams = new HttpParams({ fromObject: postParams });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post(url, serializedParams, { headers, responseType: 'text', observe: 'response' });
    }

    // We need a separate method for responseType blob because generics dont work with blobs
    postJsonDataWithBlobResponseObserveResponse(url: string, postParams: LooseObject): Observable<HttpResponse<Blob>> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post(url, postParams, { headers, responseType: 'blob', observe: 'response' });
    }

    // We need a separate method for responseType blob because generics dont work with blobs
    postJsonDataWithBlobResponseObserveBody(
        url: string,
        postParams: LooseObject,
        enableStructuredError = false
    ): Observable<Blob> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .post(url, postParams, { headers, responseType: 'blob', observe: 'body' })
            .pipe(errorResponseFormatter<Blob>(enableStructuredError));
    }

    postMultipartFormDataWithObserveBody<T>(
        url: string,
        formData: FormData,
        enableStructuredError = false
    ): Observable<T> {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .post<T>(url, formData, { headers, observe: 'body' })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    putDataObserveResponse<T>(url: string, putParams: LooseObject): Observable<HttpResponse<T>> {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.put<T>(url, putParams, { headers, observe: 'response' });
    }

    putDataObserveBody<T>(url: string, putParams: LooseObject, enableStructuredError = false): Observable<T> {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .put<T>(url, putParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    putJsonDataObserveBody<T>(url: string, putParams: LooseObject, enableStructuredError = false): Observable<T> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .put<T>(url, putParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    patchDataObserveResponse<T>(url: string, patchParams: LooseObject): Observable<HttpResponse<T>> {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.patch<T>(url, patchParams, { headers, observe: 'response' });
    }

    patchJsonDataObserveBody<T>(url: string, patchParams: LooseObject, enableStructuredError = false): Observable<T> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .patch<T>(url, patchParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    patchDataObserveBody<T>(url: string, patchParams: LooseObject, enableStructuredError = false): Observable<T> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.httpClient
            .patch<T>(url, patchParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }

    deleteDataObserveResponse<T>(url: string): Observable<HttpResponse<T>> {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.delete<T>(url, { headers, observe: 'response' });
    }

    deleteDataObserveBody<T>(url: string, enableStructuredError = false): Observable<T> {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .delete<T>(url, { headers, observe: 'body' })
            .pipe(errorResponseFormatter<T>(enableStructuredError));
    }
}
